import { useState, useCallback, useRef } from 'react';
import { pw9324Api } from '../../../services/pw9324Api';

/** Cache key format: "{type}:{code}" */
type CacheKey = string;

export interface UsePW9324LookupResult {
  deptFromName: string;
  deptToName: string;
  lookupDeptFrom: (code: string) => Promise<void>;
  lookupDeptTo: (code: string) => Promise<void>;
}

export function usePW9324Lookup(): UsePW9324LookupResult {
  const cache = useRef<Map<CacheKey, string>>(new Map());
  const [deptFromName, setDeptFromName] = useState('');
  const [deptToName, setDeptToName] = useState('');

  const lookup = useCallback(
    async (
      type: 'department' | 'employee',
      code: string,
      setter: (name: string) => void,
    ) => {
      if (!code?.trim()) {
        setter('');
        return;
      }
      const key: CacheKey = `${type}:${code.trim()}`;
      if (cache.current.has(key)) {
        setter(cache.current.get(key) ?? '');
        return;
      }
      const name = await pw9324Api.lookupName(type, code.trim());
      cache.current.set(key, name);
      setter(name);
    },
    [],
  );

  const lookupDeptFrom = useCallback(
    (code: string) => lookup('department', code, setDeptFromName),
    [lookup],
  );

  const lookupDeptTo = useCallback(
    (code: string) => lookup('department', code, setDeptToName),
    [lookup],
  );

  return { deptFromName, deptToName, lookupDeptFrom, lookupDeptTo };
}
